import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useConnection } from '@solana/wallet-adapter-react';
import { PublicKey } from '@solana/web3.js';
import { useEffect, useState } from 'react';
import { getUserTokenBalances, TokenBalance } from '@/lib/spl-token-balances';
import { supabase } from '@/integrations/supabase/client';
import { useBlockchainEvents } from '@/contexts/BlockchainEventContext';

interface EnrichedTokenBalance extends TokenBalance {
  name: string;
  ticker: string;
  image: string | null;
  price: number | null;
  valueUsd: number;
}

interface PendingTrade {
  id: string;
  type: 'buy' | 'sell';
  tokenMint: string;
  amount: number;
  solAmount: number;
  timestamp: number;
  status: 'pending' | 'confirmed' | 'failed';
}

export function usePortfolioBalances(walletPublicKey: string | null) {
  const { connection } = useConnection();
  const queryClient = useQueryClient();
  const { onTradeExecuted, onPoolUpdated, isConnected, reconnectionAttempts } = useBlockchainEvents();
  const [pendingTrades, setPendingTrades] = useState<PendingTrade[]>([]);

  const query = useQuery({
    queryKey: ['portfolio-balances', walletPublicKey],
    queryFn: async (): Promise<{
      balances: EnrichedTokenBalance[];
      solBalance: number;
      totalValueUsd: number;
    }> => {
      if (!walletPublicKey || !connection) {
        return { balances: [], solBalance: 0, totalValueUsd: 0 };
      }

      try {
        const publicKey = new PublicKey(walletPublicKey);
        const tokenBalances = await getUserTokenBalances(connection, publicKey);
        const solBalanceLamports = await connection.getBalance(publicKey);
        let solBalance = solBalanceLamports / 1e9; // Convert lamports to SOL

        // Fetch coin metadata for tokens
        const mints = tokenBalances.map(tb => tb.mint);
        const { data: coins, error: coinsError } = await supabase
          .from('coins')
          .select('mint_address, name, ticker, image, price')
          .in('mint_address', mints);

        if (coinsError) {
          console.error('Error fetching coin metadata:', coinsError);
          throw coinsError;
        }

        const coinMap = new Map(coins?.map(c => [c.mint_address, c]) || []);

        // Enrich token balances with metadata
        let enrichedBalances: EnrichedTokenBalance[] = tokenBalances.map(tb => {
          const coin = coinMap.get(tb.mint);
          const price = coin?.price || 0;
          const valueUsd = tb.uiAmount * price;
          return {
            ...tb,
            name: coin?.name || 'Unknown Token',
            ticker: coin?.ticker || 'UNK',
            image: coin?.image || null,
            price,
            valueUsd,
          };
        });

        // Apply optimistic updates for pending trades
        for (const trade of pendingTrades) {
          if (trade.status === 'pending') {
            if (trade.type === 'buy') {
              // For buy, add tokens
              const existing = enrichedBalances.find(b => b.mint === trade.tokenMint);
              if (existing) {
                existing.uiAmount += trade.amount;
                existing.valueUsd = existing.uiAmount * existing.price;
              } else {
                // Add new balance if not exists
                enrichedBalances.push({
                  mint: trade.tokenMint,
                  uiAmount: trade.amount,
                  valueUsd: trade.amount * (coinMap.get(trade.tokenMint)?.price || 0),
                  name: coinMap.get(trade.tokenMint)?.name || 'Unknown Token',
                  ticker: coinMap.get(trade.tokenMint)?.ticker || 'UNK',
                  image: coinMap.get(trade.tokenMint)?.image || null,
                  price: coinMap.get(trade.tokenMint)?.price || 0,
                  ata: '', // placeholder
                  decimals: 9, // placeholder
                  amount: trade.amount * 1e9, // placeholder
                });
              }
              // Subtract SOL
              solBalance -= trade.solAmount;
            } else if (trade.type === 'sell') {
              // For sell, subtract tokens
              const existing = enrichedBalances.find(b => b.mint === trade.tokenMint);
              if (existing) {
                existing.uiAmount -= trade.amount;
                existing.valueUsd = existing.uiAmount * existing.price;
              }
              // Add SOL
              solBalance += trade.solAmount;
            }
          }
        }

        // Fetch SOL price (assuming there's a coin entry for SOL)
        const { data: solCoin, error: solError } = await supabase
          .from('coins')
          .select('price')
          .eq('ticker', 'SOL')
          .single();

        if (solError && solError.code !== 'PGRST116') { // PGRST116 is "not found"
          console.error('Error fetching SOL price:', solError);
        }

        const solPrice = solCoin?.price || 0;
        const solValueUsd = solBalance * solPrice;
        const totalValueUsd = enrichedBalances.reduce((sum, b) => sum + b.valueUsd, 0) + solValueUsd;

        // Filter out zero balances after adjustments
        enrichedBalances = enrichedBalances.filter(b => b.uiAmount > 0);

        return {
          balances: enrichedBalances,
          solBalance,
          totalValueUsd,
        };
      } catch (error) {
        console.error('Error in portfolio balances query:', error);
        throw error;
      }
    },
    refetchInterval: 30000,
    enabled: !!walletPublicKey && !!connection,
    retry: (failureCount, error) => {
      // Retry on RPC errors, but not on auth errors
      if (error instanceof Error && error.message.includes('RPC')) {
        return failureCount < 3;
      }
      return false;
    },
  });

  // Subscribe to trade executed events to invalidate query when user's wallet is involved
  useEffect(() => {
    if (!walletPublicKey) return;

    const unsubscribeTrade = onTradeExecuted((event) => {
      if (event.data.trader === walletPublicKey) {
        queryClient.invalidateQueries({ queryKey: ['portfolio-balances', walletPublicKey] });
      }
    });

    // Subscribe to pool updates to update cached prices without refetching full balances
    const unsubscribePool = onPoolUpdated((event) => {
      // Update cached prices for the affected token
      queryClient.setQueryData(['portfolio-balances', walletPublicKey], (oldData: any) => {
        if (!oldData) return oldData;
        const updatedBalances = oldData.balances.map((balance: EnrichedTokenBalance) => {
          if (balance.mint === event.data.tokenMint) {
            // For trade events that update pool, we can use the price from the event
            // For other pool updates, we might need to refetch or estimate
            // For now, invalidate to get updated price
            // But to avoid full refetch, perhaps fetch just the price
            // Since the event syncs to database, we can refetch the price query
            // But for simplicity, invalidate the query
            queryClient.invalidateQueries({ queryKey: ['portfolio-balances', walletPublicKey] });
          }
          return balance;
        });
        return oldData; // Return unchanged, as invalidate will refetch
      });
    });

    return () => {
      unsubscribeTrade();
      unsubscribePool();
    };
  }, [onTradeExecuted, onPoolUpdated, queryClient, walletPublicKey]);

  const addPendingTrade = (trade: Omit<PendingTrade, 'id' | 'timestamp' | 'status'>) => {
    const newTrade: PendingTrade = {
      ...trade,
      id: Date.now().toString(),
      timestamp: Date.now(),
      status: 'pending',
    };
    setPendingTrades(prev => [...prev, newTrade]);
    // Auto remove after 30 seconds if not confirmed
    setTimeout(() => {
      setPendingTrades(prev => prev.filter(t => t.id !== newTrade.id));
    }, 30000);
  };

  const updatePendingTradeStatus = (id: string, status: 'confirmed' | 'failed') => {
    setPendingTrades(prev => prev.map(t => t.id === id ? { ...t, status } : t));
    // Remove after update
    setTimeout(() => {
      setPendingTrades(prev => prev.filter(t => t.id !== id));
    }, 1000);
  };

  return {
    balances: query.data?.balances || [],
    solBalance: query.data?.solBalance || 0,
    totalValueUsd: query.data?.totalValueUsd || 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    isConnected,
    reconnectionAttempts,
    pendingTrades,
    addPendingTrade,
    updatePendingTradeStatus,
  };
}